import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { Box, SxProps, useTheme } from '@mui/material';
import UploadArea, {
  UploadedFile,
  UploadedReadedFile,
} from 'shared/lib/components/controls/uploadArea/uploadArea';
import { UiContext } from 'shared/lib/contexts/uiContext';
import UploadGrid from 'pages/uploadPage/components/uploadGrid';
import map from 'lodash-es/map';
import FileItem from 'pages/uploadPage/uploadFiles/fileItem/fileItem';
import { cloneDeep, each, filter } from 'lodash-es';
import CreateFileController from 'widgets/files/createFileController';
import { useTranslation } from 'react-i18next';
import IconFolder from 'shared/icons/iconFolder';
import { filesApi } from 'entities/file/api/filesApi';
import { InitialPlacement } from 'entities/types';
import { isFileIgnored } from 'shared/lib/components/controls/uploadArea/components/utils';

export type UploadedFileWithPreview = UploadedFile & {
  previewFile?: UploadedReadedFile;
  error?: string;
};

export default observer(function UploadFiles({
  isVisible,
  initialSectionId,
  initialFolderId,
  initialName,
}: {
  isVisible: boolean;
} & InitialPlacement) {
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const theme = useTheme();
  const { t } = useTranslation();

  const [files, setFiles] = useState<UploadedFileWithPreview[]>([]);

  const gridSx: SxProps = isVisible
    ? files.length
      ? {
          'gridTemplateAreas': "'left right' 'bottom bottom'",
          '& > :nth-of-type(1)': {
            gridArea: 'left',
          },
          '& > :nth-of-type(2)': {
            gridArea: 'right',
          },
          '& > :nth-of-type(3)': {
            gridArea: 'bottom',
          },
        }
      : {
          'gridTemplateAreas': "'bottom bottom'",
          '& > :nth-of-type(1)': {
            gridArea: 'bottom',
          },
        }
    : {
        visibility: 'hidden',
        minHeight: 0,
        maxHeight: 0,
        height: 0,
        overflow: 'hidden',
      };

  return (
    <UploadGrid sx={gridSx}>
      {files.length ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: sp.s30,
          }}
        >
          {map(files, (file, i) => {
            return (
              <FileItem
                error={file.error}
                key={file.originalFile.name}
                file={file}
                onDelete={() =>
                  setFiles((curr) => [
                    ...curr.slice(0, i),
                    ...curr.slice(i + 1),
                  ])
                }
                preview={file.previewFile}
                onSetPreview={(preview) => {
                  setFiles((curr) => {
                    const clone = cloneDeep(curr);
                    clone[i].previewFile = preview;

                    return clone;
                  });
                }}
              ></FileItem>
            );
          })}
        </Box>
      ) : null}

      <CreateFileController
        uploadModalText="Загрузка файлов"
        getFiles={() => files}
        isOnlyModal={files.length === 0}
        saveBlockProps={{
          initialSectionId,
          initialFolderId,
          initialName,
          onCancel: () => {
            setFiles([]);
          },
        }}
        onFileSuccess={(name) =>
          setFiles((curr) => {
            return [
              ...filter(curr, (item) => {
                return item.originalFile.name !== name;
              }),
            ];
          })
        }
        onFileError={(name, error) =>
          setFiles((curr) => {
            return [
              ...map(curr, (item) => {
                const isThis = item.originalFile.name === name;

                return { ...item, error: isThis ? error : item.error };
              }),
            ];
          })
        }
        onUpload={(params, onProgress) => {
          if (params.folder_id) {
            return filesApi.createInFolder(params, onProgress);
          }

          return filesApi.createInSection(params, onProgress);
        }}
      ></CreateFileController>

      <UploadArea
        isDataUrl={false}
        accept={{}}
        areMultipleUploadsAllowed={true}
        innerSx={{ height: 550 }}
        text={t('noCategory.uploadFileHelp')}
        buttonText={t('actions.filesUpload')}
        icon={
          <IconFolder sizeKey="48x48" extColor={theme.palette.text.secondary} />
        }
        handleAddFile={(newFile) => {
          setFiles((curr) => {
            if (isFileIgnored(newFile.originalFile.name)) {
              return curr;
            }

            each(curr, (file) => {
              if (file.originalFile.name === newFile.originalFile.name) {
                return curr;
              }
            });

            return [...curr, newFile];
          });
        }}
      ></UploadArea>
    </UploadGrid>
  );
});
